import { useEffect, useState } from 'react';

/*
 * Shared state for the platform tour - the workspace-page walk that
 * continues after the goal-page tour finishes. A tiny module-level store
 * (sessionStorage-backed) rather than Redux: three consumers, no actions,
 * and it must be writable from non-connected components.
 *
 * Shape: { phase: 'platform', idx: <PLATFORM_STEPS index> } or null.
 */

const KEY = 'predictai.tour.platform';

// The post-goal walk across the workspace, in presentation order. Each
// step's card renders on (or points to) `path`; the sidebar pulses the
// matching item so users anchor page <-> nav.
export const PLATFORM_STEPS = [
  {
    path: '/signals',
    label: 'Data Signals',
    title: 'Your signal universe',
    explain: 'Every series in the workspace lives here — values, freshness, stats. Click any signal to inspect and chart it.',
  },
  {
    path: '/discovery',
    label: 'Discovery',
    title: 'Discovery',
    explain: 'Open-ended AI insights over all your signals — patterns, shifts and anomalies surface here on their own.',
  },
  {
    path: '/relationships',
    label: 'Relationships',
    title: 'Relationships',
    explain: 'How your signals move together — correlations and causal links, mapped so you can see structure at a glance.',
  },
  {
    path: '/segments',
    label: 'Segments',
    title: 'Segments',
    explain: 'Training-ready datasets on a time grid. Goals build one automatically; you can edit or create your own here.',
  },
  {
    path: '/models',
    label: 'Models',
    title: 'Models',
    explain: 'The model library — foundation, custom and external models. Everything model-shaped starts here.',
  },
  {
    path: '/pipelines',
    label: 'Pipelines',
    title: 'Pipelines',
    explain: 'Scheduled training: a model pool plus a segment, raced on every run. Fresh trainings come out the other side.',
  },
  {
    path: '/trainings',
    label: 'Trainings',
    title: 'Trainings',
    explain: 'Every training run across the workspace, scored and comparable — deploy any of them to serving.',
  },
  {
    path: '/deployments',
    label: 'Deployments',
    title: 'Serving',
    explain: 'Deployed models serving live predictions. This is where trained models go to work.',
  },
  {
    path: '/inference',
    label: 'Inferences',
    title: 'Inferences',
    explain: 'Every prediction served, logged with its inputs and outputs — your audit trail.',
  },
  {
    path: '/datasources',
    label: 'Data Sources',
    title: 'Add any data, anytime',
    explain: 'CSV uploads, live pushes, connectors — everything comes in here. You\u2019re all set: bring your own data whenever you\u2019re ready.',
  },
];

const read = () => {
  try {
    const v = JSON.parse(sessionStorage.getItem(KEY) || 'null');
    if (v && v.phase === 'platform' && Number.isInteger(v.idx)
        && v.idx >= 0 && v.idx < PLATFORM_STEPS.length) return v;
  } catch (_e) { /* fall through */ }
  return null;
};

let state = null;
let loaded = false;
const subs = new Set();

const ensure = () => {
  if (!loaded) { state = read(); loaded = true; }
  return state;
};

export const getTour = () => ensure();

export const setTour = (next) => {
  ensure();
  state = next;
  try {
    if (next) sessionStorage.setItem(KEY, JSON.stringify(next));
    else sessionStorage.removeItem(KEY);
  } catch (_e) { /* private mode - in-memory still works this tab */ }
  subs.forEach((f) => f(state));
};

export const useTour = () => {
  const [s, setS] = useState(ensure);
  useEffect(() => {
    const f = (v) => setS(v);
    subs.add(f);
    setS(ensure());
    return () => subs.delete(f);
  }, []);
  return s;
};
